import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { TRANSACTION_HEADER_SAFE_COLUMNS } from "@/lib/queries";

/** Pestañas de "Deudas del hogar", en el search param `tab`. Un valor inválido cae en `resumen`. */
export const IOU_TABS = ["resumen", "mes", "miembros"] as const;
export type IouTab = (typeof IOU_TABS)[number];
export const iouSearchSchema = z.object({
  tab: fallback(z.enum(IOU_TABS), "resumen").default("resumen"),
});
export const iouSearchValidator = zodValidator(iouSearchSchema);

export interface IouHeader {
  id: string;
  household_id: string;
  created_by: string;
  account_id: string | null;
  related_account_id: string | null;
  date: string;
  total_amount: number;
  description: string;
  type: string;
  exchange_rate: number;
  created_at: string;
  debtor_id: string | null;
  creditor_id: string | null;
  settled_at: string | null;
  settled_with_header_id: string | null;
  settle_method: "cash" | "forgive" | null;
  accounting_only: boolean;
  source_bank_expense_id: string | null;
  source_cc_payment_id: string | null;
  /** Compra con tarjeta de la que nació el préstamo (opcional: lo traen sólo las lecturas con TRANSACTION_HEADER_SAFE_COLUMNS). */
  source_cc_purchase_id?: string | null;
}

export async function fetchIous(householdId: string): Promise<IouHeader[]> {
  const { data, error } = await supabase
    .from("transaction_headers")
    // Rebote #170 ronda 4, FIX 1: `select("*")` viola el GRANT column-level de transaction_headers
    // (refund_of_header_id ya no es legible por ningún select directo, ver migración
    // 20260927060203). TRANSACTION_HEADER_SAFE_COLUMNS no incluye esa columna: fetchIous no la usa.
    .select(TRANSACTION_HEADER_SAFE_COLUMNS)
    .eq("household_id", householdId)
    .eq("type", "iou" as any)
    .order("date", { ascending: false });
  if (error) throw error;
  return (data ?? []) as any;
}

/**
 * Moneda de cada préstamo ABIERTO del hogar (id del préstamo → moneda de la cuenta desde la que se prestó).
 * Sale de un RPC porque la RLS esconde la cuenta personal del otro miembro: con las cuentas que ve el
 * cliente, cada miembro adivinaba una moneda distinta. Un préstamo sin moneda conocida no figura en el mapa.
 */
export async function fetchIouCurrencies(): Promise<Map<string, string>> {
  const { data, error } = await supabase.rpc("get_household_iou_currencies");
  if (error) throw error;
  const map = new Map<string, string>();
  for (const row of (data ?? []) as { header_id: string; currency: string | null }[]) {
    if (row.currency) map.set(row.header_id, row.currency);
  }
  return map;
}

/** Préstamos abiertos cuya moneda no se sabe: no se suman ni se clasifican, se avisan. */
export function iousWithoutCurrency(ious: IouHeader[], currencyByIouId: Map<string, string>): IouHeader[] {
  return ious.filter(
    (i) => !i.settled_at && i.creditor_id && i.debtor_id && !currencyByIouId.has(i.id),
  );
}

/**
 * Compute net balance per (creditor → debtor, currency) considering only OPEN ious.
 * La moneda viene de `currencyByIouId` (igual para los dos miembros); un préstamo sin moneda se salta:
 * nunca se adivina una.
 */
export interface PairBalance {
  creditorId: string;
  debtorId: string;
  currency: string;
  amount: number; // always positive; direction is creditor->debtor
}

export function computeBalances(ious: IouHeader[], currencyByIouId: Map<string, string>): PairBalance[] {
  // Map key: "creditor|debtor|currency" -> amount
  const map = new Map<string, number>();
  ious.filter((i) => !i.settled_at && i.creditor_id && i.debtor_id).forEach((i) => {
    const cur = currencyByIouId.get(i.id);
    if (!cur) return;
    const key = `${i.creditor_id}|${i.debtor_id}|${cur}`;
    map.set(key, (map.get(key) ?? 0) + Number(i.total_amount));
  });
  // Net out reverse pairs (A owes B and B owes A in same currency)
  const result: PairBalance[] = [];
  const seen = new Set<string>();
  for (const [key, amount] of map) {
    if (seen.has(key)) continue;
    const [a, b, cur] = key.split("|");
    const reverseKey = `${b}|${a}|${cur}`;
    const reverse = map.get(reverseKey) ?? 0;
    seen.add(key); seen.add(reverseKey);
    const net = amount - reverse;
    if (Math.abs(net) < 0.01) continue;
    if (net > 0) result.push({ creditorId: a, debtorId: b, currency: cur, amount: net });
    else result.push({ creditorId: b, debtorId: a, currency: cur, amount: -net });
  }
  return result;
}

/** Mark an IOU as settled by linking to the header that closed it. */
export async function markIouSettled(iouId: string, settleMethod: "cash" | "forgive", settledWithHeaderId: string) {
  const { error } = await supabase
    .from("transaction_headers")
    .update({
      settled_at: new Date().toISOString(),
      settle_method: settleMethod,
      settled_with_header_id: settledWithHeaderId,
    } as any)
    .eq("id", iouId);
  if (error) throw error;
}

/** Lo que se necesita de una compra con tarjeta para explicar un préstamo que nació de ella. */
export interface IouPurchase {
  id: string;
  description: string;
  total_amount: number;
  purchase_date: string;
  installments_count: number;
  account_id: string;
  currency: string;
}

/**
 * Compras con tarjeta detrás de los préstamos (por id). Sólo trae lo que la RLS deja leer a quien mira:
 * una compra que no vuelve no se inventa, el presentador la marca "Detalle no disponible".
 * Mira `error` antes que `data`: una falla no se lee como "no hay compras".
 */
export async function fetchIouPurchases(ids: string[]): Promise<Map<string, IouPurchase>> {
  const map = new Map<string, IouPurchase>();
  if (ids.length === 0) return map;
  const { data, error } = await supabase
    .from("cc_purchases")
    .select("id, description, total_amount, purchase_date, installments_count, account_id, currency")
    .in("id", ids);
  if (error) throw error;
  for (const r of (data ?? []) as IouPurchase[]) map.set(r.id, r);
  return map;
}

/** Ids (sin repetir, ordenados) de las compras con tarjeta de los préstamos dados. */
export function iouPurchaseIds(ious: IouHeader[]): string[] {
  return [...new Set(ious.map((i) => i.source_cc_purchase_id).filter((x): x is string => !!x))].sort();
}

export type IouPurchasesStatus = "loading" | "error" | "ready";

export type IouDetail =
  /** Préstamo manual o de banco: ya trae su descripción completa. */
  | { kind: "none" }
  | { kind: "loading" }
  | { kind: "error" }
  /** El préstamo nació de una compra con tarjeta pero ésta no se pudo leer (la RLS la esconde). */
  | { kind: "unavailable" }
  | {
      kind: "purchase";
      merchant: string;
      total: number;
      currency: string;
      accountId: string;
      date: string;
      /** % de la compra que se debe = monto del préstamo / total de la compra (no el snapshot). */
      sharePct: number | null;
    };

/** Qué mostrar de un préstamo. Si la compra no vuelve, se dice; nunca se cae al texto genérico en silencio. */
export function presentIouDetail(
  iou: Pick<IouHeader, "source_cc_purchase_id" | "total_amount">,
  purchases: { status: IouPurchasesStatus; byId?: Map<string, IouPurchase> },
): IouDetail {
  if (!iou.source_cc_purchase_id) return { kind: "none" };
  if (purchases.status === "loading") return { kind: "loading" };
  if (purchases.status === "error") return { kind: "error" };
  const p = purchases.byId?.get(iou.source_cc_purchase_id);
  if (!p) return { kind: "unavailable" };
  const total = Number(p.total_amount);
  const owed = Number(iou.total_amount);
  const sharePct =
    Number.isFinite(total) && total > 0 && Number.isFinite(owed)
      ? Math.round((owed / total) * 10000) / 100
      : null;
  return {
    kind: "purchase",
    merchant: p.description,
    total,
    currency: p.currency,
    accountId: p.account_id,
    date: p.purchase_date,
    sharePct,
  };
}

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/** "2026-07-12" → "12-jul" (sin pasar por Date: no hay corrimiento de zona horaria). */
export function shortIouDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const mes = MESES_CORTOS[Number(m[2]) - 1];
  return mes ? `${m[3]}-${mes}` : iso;
}

/** 90.88 → "90,88 %" (coma decimal, sin ceros de más: 50 → "50 %"). */
export function formatSharePct(pct: number): string {
  return `${String(Math.round(pct * 100) / 100).replace(".", ",")} %`;
}

/**
 * Línea de detalle lista para mostrar, p. ej.
 * "Compra BODEGA SAN RAFAEL · $620,21 en Nu Bank Crédito · 12-jul · le toca 90,88 %".
 * `accountName` = nombre de la cuenta si quien mira la ve (la RLS esconde la tarjeta personal del otro:
 * entonces se omite el "en …", no se inventa). `null` para `none`: el que llama usa su `description`.
 */
export function iouDetailText(
  detail: IouDetail,
  opts: { accountName?: string | null; formatMoney: (amount: number, currency: string) => string },
): string | null {
  switch (detail.kind) {
    case "none":
      return null;
    case "loading":
      return "Cargando detalle…";
    case "error":
      return "No pudimos cargar el detalle";
    case "unavailable":
      return "Detalle no disponible";
    case "purchase": {
      const parts = [
        `Compra ${detail.merchant} · ${opts.formatMoney(detail.total, detail.currency)}${opts.accountName ? ` en ${opts.accountName}` : ""}`,
        shortIouDate(detail.date),
      ];
      if (detail.sharePct !== null) parts.push(`le toca ${formatSharePct(detail.sharePct)}`);
      return parts.join(" · ");
    }
  }
}
